import { VISION_REMOTE_STAGES, VisionRemoteStage } from 'enums/calibration';
import {
  IVisionCameraConfiguration,
  IVisionRemoteCalibrationResponse,
} from 'lib_ts/interfaces/machine-msg/vision/i-remote-calibration';
import { create } from 'zustand';

interface VisionRemoteState {
  loading: boolean; // when we're waiting on a M2U_VisionRemoteCalibrationResponse after sending a recalculate or start request
  saving: boolean; // when we're waiting on a M2U_VisionRemoteCalibrationResponse after saving (e.g. to reset sliders after)
  lastResponse?: IVisionRemoteCalibrationResponse;
  stage: VisionRemoteStage;
  leaderSliders: IVisionCameraConfiguration;
  followerSliders: IVisionCameraConfiguration;
}

interface VisionRemoteActions {
  setLoading: (loading: boolean) => void;
  setSaving: (saving: boolean) => void;
  setLastResponse: (payload: IVisionRemoteCalibrationResponse) => void;
  setStage: (stage: VisionRemoteStage) => void;
  nextStage: () => void;
  prevStage: () => void;
  setLeaderSliders: (config: IVisionCameraConfiguration) => void;
  setFollowerSliders: (config: IVisionCameraConfiguration) => void;
  reset: () => void;
}

type VisionRemoteStore = VisionRemoteState & VisionRemoteActions;

export const DEFAULT_CAMERA_CONFIG: IVisionCameraConfiguration = {
  x_mm: 0,
  y_mm: 0,
  z_mm: 0,
  roll_deg: 0,
  pitch_deg: 0,
  yaw_deg: 0,
};

const initialState: VisionRemoteState = {
  lastResponse: undefined,
  loading: false,
  saving: false,
  stage: VisionRemoteStage.Overview,
  leaderSliders: DEFAULT_CAMERA_CONFIG,
  followerSliders: DEFAULT_CAMERA_CONFIG,
};

const getStageIndex = (stage: VisionRemoteStage) =>
  VISION_REMOTE_STAGES[stage].index;

const getStageByIndex = (index: number): VisionRemoteStage | null => {
  const stage = Object.values(VISION_REMOTE_STAGES).find(
    (s) => s.index === index
  );

  if (!stage) {
    return null;
  }

  return stage.value;
};

const useVisionRemoteStore = create<VisionRemoteStore>((set, get) => ({
  ...initialState,
  setLoading: (loading: boolean) => {
    set(() => ({ loading }));
  },
  setSaving: (saving: boolean) => {
    set(() => ({ saving }));
  },
  setStage: (stage: VisionRemoteStage) => {
    set(() => ({ stage }));
  },
  nextStage: () => {
    const { stage } = get();

    const currentIndex = getStageIndex(stage);
    const nextIndex = currentIndex + 1;

    const nextStage = getStageByIndex(nextIndex);

    if (!nextStage) {
      console.error('Invalid next stage', {
        currentStage: stage,
        nextIndex,
        currentIndex,
      });
      return;
    }

    set(() => ({ stage: nextStage }));
  },
  setLastResponse: (payload: IVisionRemoteCalibrationResponse) => {
    set(() => ({ lastResponse: payload }));
  },
  setLeaderSliders: (config: IVisionCameraConfiguration) => {
    set(() => ({ leaderSliders: config }));
  },
  setFollowerSliders: (config: IVisionCameraConfiguration) => {
    set(() => ({ followerSliders: config }));
  },
  prevStage: () => {
    const { stage } = get();

    const currentIndex = getStageIndex(stage);
    const prevIndex = currentIndex - 1;

    const prevStage = getStageByIndex(prevIndex);

    if (!prevStage) {
      console.error('Invalid previous stage', {
        currentStage: stage,
        prevIndex,
        currentIndex,
      });
      return;
    }

    set(() => ({ stage: prevStage }));
  },
  reset: () => set(initialState),
}));

export default useVisionRemoteStore;
